import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, QrCode, ArrowRightLeft, ChevronLeft, CreditCard, RotateCcw, ShoppingBag, Percent, Ban } from "lucide-react";
import { useApp } from "@/App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatAssetAmount } from "@/lib/amountFormat";
import { getCryptoIconFallback, getCryptoIconUrl } from "@/lib/cryptoIcons";
import OperationSheet from "@/components/cards/OperationSheet";
import MerchantIcon, { matchBrand } from "@/components/cards/MerchantIcon";
import { categoryIconFor } from "@/components/cards/CategoryIcon";
import { mccCategory } from "@/components/cards/mccCategories";

function parseMeta(metaJson) {
  if (!metaJson) return {};
  if (typeof metaJson === "object") return metaJson;
  try {
    const parsed = JSON.parse(metaJson);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeNetworkLabel(raw) {
  const text = String(raw || "").trim().toLowerCase();
  if (!text) return null;
  if (text.includes("trc20") || text.includes("trx") || text.includes("tron")) return "TRC20";
  if (text.includes("erc20") || text.includes("eth") || text.includes("ethereum")) return "ERC20";
  if (text.includes("bep20") || text.includes("bsc") || text.includes("bnb")) return "BEP20";
  if (text.includes("ton")) return "TON";
  if (text.includes("sol")) return "SOL";
  if (text.includes("btc") || text.includes("bitcoin")) return "BTC";
  return text.toUpperCase();
}

function parseFiatRequisites(rawAddress) {
  const parts = {};
  const raw = String(rawAddress || "");
  for (const chunk of raw.split(",")) {
    if (!chunk.includes(":")) continue;
    const [k, ...rest] = chunk.split(":");
    const key = String(k || "").trim().toLowerCase();
    const value = rest.join(":").trim();
    if (key) parts[key] = value;
  }
  const methodRaw = String(parts.method || "").trim().toLowerCase();
  const method = methodRaw.includes("sbp")
    ? "sbp"
    : methodRaw.includes("card")
      ? "card"
      : null;
  let rub = null;
  const rubRaw = String(parts.rub || "").trim();
  if (rubRaw) {
    const parsed = Number(rubRaw.replace(/\s+/g, "").replace(",", "."));
    if (Number.isFinite(parsed)) rub = parsed;
  }
  return {
    method,
    rub,
    bank: String(parts.bank || "").trim() || null,
    phone: String(parts.phone || "").trim() || null,
    card: String(parts.card || "").trim() || null,
    name: String(parts.name || "").trim() || null,
    hasParsed: Object.keys(parts).length > 0,
  };
}

function mapBackendTxToUI(tx) {
  const id = tx.transaction_id ?? tx.id;
  const direction = String(tx.direction || "").toLowerCase();
  const amount = Number(tx.amount ?? tx.amount_usd ?? 0);
  const refType = String(tx.ref_type || "").toLowerCase();
  const meta = parseMeta(tx.meta_json);
  const walletRaw = tx.wallet_address || meta.wallet_address || meta.address || null;
  const fiatReq = parseFiatRequisites(walletRaw);

  let type = direction === "credit" ? "deposit" : "payment";
  let hidden = false;

  // Зарубежная карта. Идём ПЕРВЫМИ: без этого card_* проваливались в общий
  // хвост и подписывались как QR-платёж.
  //
  // Опираемся на card_op, а не на ref_type: списание за выпуск и за пополнение
  // карты долго писались одним card_issue, и у старых строк ref_type врёт.
  // card_op бэкенд восстанавливает по scope ключа идемпотентности.
  if (refType.startsWith("card_")) {
    const op = String(tx.card_op || refType.slice(5).replace("_refund", "")).toLowerCase();
    if (refType === "card_fee_refund") type = "card_fee_refund";
    else if (refType === "card_closed_residual") type = "card_unload";
    else if (refType.endsWith("_refund")) type = op === "fund" ? "card_fund_refund" : "card_issue_refund";
    else if (op === "unload" || op === "close") type = "card_unload";
    else if (op === "fund") type = "card_fund";
    else type = "card_issue";
  }
  // Fortune transactions
  else if (refType.includes("fortune") || refType.includes("magic_cup")) {
    type = direction === "credit" || refType.includes("win") ? "fortune_win" : "fortune_spin";
  }
  // Swaps
  else if (refType.includes("swap")) {
    type = "swap";
    // Show only resulting credit row for swaps.
    if (refType.includes("swap_debit") || refType.includes("swap_fee")) hidden = true;
  }
  // Withdrawals
  else if (direction !== "credit" && (refType.includes("withdraw") || refType.includes("send") || refType.includes("internal_transfer_out"))) {
    type = "withdrawal";
  }
  const isFiatWithdrawal =
    type === "withdrawal" &&
    (
      String(meta.method || "").toLowerCase() === "fiat" ||
      fiatReq.method !== null ||
      (walletRaw && /method\s*:/i.test(String(walletRaw)))
    );

  // Backend returns created_at as TEXT datetime('now'), but some older rows might be epoch.
  // Normalize to ISO.
  let createdAt = new Date();
  if (tx.created_at != null) {
    const v = tx.created_at;
    const n = Number(v);
    if (Number.isFinite(n) && n > 1e9) createdAt = new Date(n * 1000);
    else createdAt = new Date(v);
  }

  return {
    id,
    hidden,
    type,
    status: tx.status || "completed",
    amount,
    currency: tx.currency || "USDT",
    network: isFiatWithdrawal ? null : normalizeNetworkLabel(String(
      tx.network ||
      meta.network ||
      meta.chain ||
      meta.network_name ||
      meta.chain_name ||
      meta.chainId ||
      meta.pay_network ||
      meta.blockchain ||
      ""
    )),
    is_fiat_withdrawal: isFiatWithdrawal,
    is_fiat_topup: refType === "fiat_topup_credit",
    fiat_method: fiatReq.method,
    amount_rub: Number(tx.amount_rub ?? meta.rub_amount_exact ?? meta.rub_amount ?? fiatReq.rub) || null,
    swap_from_currency: String(tx.from_currency || meta.from_currency || "").toUpperCase() || null,
    swap_from_amount: Number(tx.from_amount ?? meta.from_amount ?? 0) || null,
    swap_to_currency: String(tx.to_currency || meta.to_currency || "").toUpperCase() || null,
    swap_to_amount: Number(tx.to_amount ?? meta.to_amount ?? 0) || null,
    created_at: createdAt.toISOString(),
    ref_type: tx.ref_type,
    ref_id: tx.ref_id,
    meta_json: tx.meta_json,
  };
}

// Операция самой карты — покупка, комиссия, возврат, отказ.
//
// Берём ТОЛЬКО строки провайдера (source === "card"). Наши пополнения и выводы
// лента карты тоже отдаёт, но они уже лежат в леджере кошелька отдельными
// строками: взять их и отсюда значит показать одну операцию дважды.
function mapCardOpToUI(op, card) {
  if (!op || op.source !== "card") return null;
  const scale = Number.isFinite(Number(op.scale)) ? Number(op.scale) : 2;
  const declined = op.status === "declined" || (card.provider === "izipay" && op.status === "failed");
  const kind = declined ? "decline" : op.type === "fee" ? "fee"
    : op.type === "refund" || op.type === "reversal" ? "refund" : "purchase";
  return {
    // Ключ не может быть просто op.id: в общем списке рядом лежат строки
    // кошелька со своей нумерацией, и они бы столкнулись.
    id: `card:${card.cardId}:${op.id}`,
    hidden: false,
    type: "card_op",
    status: op.status || "completed",
    amount: Number(op.amountMinor || 0) / 10 ** scale,
    currency: String(op.currency || "USD").toUpperCase(),
    incoming: op.direction === "in",
    card_kind: kind,
    card_merchant: declined ? null : op.merchantName || null,
    card_mcc: op.mcc ?? null,
    card_mcc_text: op.mccDescription || null,
    card_last4: card.last4 || null,
    card_raw: op,
    created_at: op.occurredAt ? new Date(op.occurredAt).toISOString() : new Date(0).toISOString(),
  };
}

// Значок карточной операции по той же очереди, что и в ленте самой карты:
// логотип бренда, затем категория по MCC, затем монограмма.
function CardOpGlyph({ op }) {
  const declined = op?.status === "declined";
  const merchant = op?.type !== "fee" && !declined ? op?.merchantName : null;
  if (op?.type === "fee") return <Percent className="w-5 h-5 text-amber-500" />;
  if (declined) return <Ban className="w-5 h-5 text-red-500" />;
  if (op?.type === "refund" || op?.type === "reversal") {
    return <RotateCcw className="w-5 h-5 text-green-500" />;
  }
  if (merchant && matchBrand(merchant)) return <MerchantIcon name={merchant} />;
  const Glyph = merchant ? categoryIconFor(op?.mcc) : null;
  if (Glyph) return <Glyph className="w-5 h-5 text-muted-foreground" />;
  if (merchant) return <MerchantIcon name={merchant} />;
  return <ShoppingBag className="w-5 h-5 text-muted-foreground" />;
}

function formatDateTimeParts(rawDate) {
  const d = new Date(rawDate);
  const date = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { date, time };
}

function mapNetworkIconSymbol(network, currency) {
  const net = String(network || "").trim().toUpperCase();
  if (!net) return String(currency || "USDT").toUpperCase();
  if (net.includes("TRC") || net === "TRX") return "TRX";
  if (net.includes("ERC") || net === "ETH") return "ETH";
  if (net.includes("BEP") || net.includes("BSC") || net === "BNB") return "BNB";
  if (net.includes("TON")) return "TON";
  if (net.includes("SOL")) return "SOL";
  if (net.includes("BTC")) return "BTC";
  return String(currency || "USDT").toUpperCase();
}

export default function HistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  // Подробности карточной операции показываем тем же листом, что и в разделе
  // карт: там уже разобраны категория, код отказа и идентификатор.
  const [sheet, setSheet] = useState(null);

  const typeCfg = useMemo(
    () => ({
      deposit: { icon: ArrowDownLeft, sign: "+", color: "text-green-500" },
      withdrawal: { icon: ArrowUpRight, sign: "-", color: "text-red-500" },
      payment: { icon: QrCode, sign: "-", color: "text-blue-500" },
      fortune_spin: { icon: QrCode, sign: "-", color: "text-purple-500" },
      fortune_win: { icon: ArrowDownLeft, sign: "+", color: "text-yellow-500" },
      swap: { icon: ArrowRightLeft, sign: "", color: "text-indigo-500" },
      card_issue: { icon: CreditCard, sign: "-", color: "text-sky-500" },
      card_fund: { icon: CreditCard, sign: "-", color: "text-sky-500" },
      card_unload: { icon: CreditCard, sign: "+", color: "text-green-500" },
      card_issue_refund: { icon: RotateCcw, sign: "+", color: "text-green-500" },
      card_fee_refund: { icon: RotateCcw, sign: "+", color: "text-green-500" },
      card_fund_refund: { icon: RotateCcw, sign: "+", color: "text-green-500" },
      card_op: { icon: ShoppingBag, sign: "-", color: "text-foreground" },
    }),
    []
  );

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const [txRes, cardsRes] = await Promise.all([
          api.get("/transactions", { params: { limit: 50, offset: 0 } }),
          // История кошелька не должна падать из-за карт: у большинства их нет,
          // а раздел может быть выключен целиком.
          api.get("/cards/list").catch(() => null),
        ]);

        const wallet = (txRes?.data?.transactions ?? [])
          .map(mapBackendTxToUI)
          .filter((tx) => !tx.hidden);

        const cards = (cardsRes?.data?.items ?? []).filter((c) => c && c.cardId);
        const cardOps = (
          await Promise.all(
            cards.map((card) =>
              api
                .get(`/cards/${card.cardId}/transactions`)
                // Молчание по одной карте не должно уносить всю историю.
                .then((r) => (r?.data?.items ?? []).map((op) => mapCardOpToUI(op, card)))
                .catch(() => [])
            )
          )
        )
          .flat()
          .filter(Boolean);

        setItems(
          [...wallet, ...cardOps].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
        );
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("common.error"));
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(-1);
  }, [haptic, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="history-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
        {showInlineBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold text-foreground">{t("history.title")}</h1>
        <span aria-hidden="true" className="h-8 w-8" />
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5.5rem+var(--lp-bottom))]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : !items.length ? (
          <div className="px-4 py-10 text-center text-muted-foreground">
            {t("history.empty") || "No transactions yet"}
          </div>
        ) : (
          <div className="px-4 pt-6 space-y-2.5">
            {items.map((tx) => {
        const cfg = typeCfg[tx.type] || typeCfg.payment;
        const Icon = cfg.icon;
        const isCardOp = tx.type === "card_op";
        const isSwap = tx.type === "swap";
        const isDeposit = tx.type === "deposit";
        const isWithdrawal = tx.type === "withdrawal";
        const txCurrency = String(
          isSwap ? (tx.swap_to_currency || tx.currency || "USDT") : (tx.currency || "USDT")
        ).toUpperCase();
        const swapFrom = String(tx.swap_from_currency || tx.currency || "USDT").toUpperCase();
        const swapTo = String(tx.swap_to_currency || tx.currency || "USDT").toUpperCase();
        const netSymbol = mapNetworkIconSymbol(tx.network, txCurrency);
        const absAmount = Math.abs(Number(isSwap ? (tx.swap_to_amount ?? tx.amount) : tx.amount) || 0);
        // У карточной операции знак задаёт её направление, а не тип строки:
        // возврат приходит тем же типом, что и покупка.
        const sign = isCardOp ? (tx.incoming ? "+" : "-") : cfg.sign || "";
        const amountColor = isCardOp
          ? tx.incoming
            ? "text-green-500"
            : "text-foreground"
          : cfg.color;
        const amountStr = `${sign}${formatAssetAmount(absAmount, txCurrency, {
          // Доллар карты — такие же деньги, как USDT: копейки не прячем.
          minDecimals: ["USDT", "USDC", "USD"].includes(txCurrency) ? 2 : 0,
          maxDecimals: tx.is_fiat_topup ? 6 : undefined,
        })}`;
        const { date, time } = formatDateTimeParts(tx.created_at);
        let title = t("tx.payment");
        if (tx.type === "deposit") title = t(tx.is_fiat_topup ? "tx.deposit_rub" : "tx.deposit");
        else if (tx.type === "withdrawal") title = t("tx.withdrawal");
        if (tx.type === "withdrawal" && tx.is_fiat_withdrawal) {
          title = t("tx.withdrawal_rub", "Вывод рублей");
        }
        else if (tx.type === "fortune_spin") title = t("tx.fortune_spin");
        else if (tx.type === "fortune_win") title = t("tx.fortune_win");
        else if (tx.type === "swap") title = t("tx.swap");
        else if (tx.type === "card_issue") title = t("tx.card_issue");
        else if (tx.type === "card_fund") title = t("tx.card_fund");
        else if (tx.type === "card_unload") title = t("tx.card_unload");
        else if (tx.type === "card_issue_refund") title = t("tx.card_issue_refund");
        else if (tx.type === "card_fee_refund") title = t("tx.card_fee_refund");
        else if (tx.type === "card_fund_refund") title = t("tx.card_fund_refund");
        else if (isCardOp) {
          // Имя мерчанта приходит из внешнего источника — показываем как есть,
          // ветвиться по нему нельзя.
          title =
            tx.card_merchant ||
            (tx.card_kind === "fee"
              ? t("tx.card_fee")
              : tx.card_kind === "refund"
                ? t("tx.card_refund")
                : tx.card_kind === "decline"
                  ? t("tx.card_decline")
                  : t("tx.card_purchase"));
        }
        else if (tx.type === "payment") title = t("tx.qr_payment") || t("tx.payment");
              return (
                <button
                  key={String(tx.id)}
                  className={cn(
                    "lp-glass w-full text-left p-3 flex items-center gap-2.5",
                    "hover:bg-[var(--lp-tile)] transition-colors"
                  )}
                  onClick={() => {
                    haptic?.("light");
                    // У карточной операции нет строки в леджере кошелька,
                    // и /transaction/{id} про неё ничего не знает.
                    if (isCardOp) {
                      setSheet({ op: tx.card_raw, last4: tx.card_last4 });
                      return;
                    }
                    navigate(`/transaction/${tx.id}`);
                  }}
                >
            <div className="w-10 h-10 rounded-full bg-[var(--lp-tile)] flex items-center justify-center shrink-0 overflow-hidden">
              {isCardOp ? <CardOpGlyph op={tx.card_raw} /> : <Icon className={cn("w-5 h-5", cfg.color)} />}
            </div>

            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
              <div className="min-w-0 pr-1">
                <div className="font-semibold text-[17px] leading-tight truncate">{title}</div>
                {isSwap && (
                  <div className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground flex-wrap">
                    <img
                      src={getCryptoIconUrl(swapFrom)}
                      alt={swapFrom}
                      className="w-3.5 h-3.5 rounded-full"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getCryptoIconFallback(swapFrom);
                      }}
                    />
                    <span>{swapFrom}</span>
                    <span>→</span>
                    <img
                      src={getCryptoIconUrl(swapTo)}
                      alt={swapTo}
                      className="w-3.5 h-3.5 rounded-full"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getCryptoIconFallback(swapTo);
                      }}
                    />
                    <span>{swapTo}</span>
                  </div>
                )}
                {(isDeposit || isWithdrawal) && (
                  <div className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground whitespace-nowrap">
                    <img
                      src={getCryptoIconUrl(txCurrency)}
                      alt={txCurrency}
                      className="w-3.5 h-3.5 rounded-full"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getCryptoIconFallback(txCurrency);
                      }}
                    />
                    <span>{txCurrency}</span>
                    {isWithdrawal && tx.is_fiat_withdrawal && tx.fiat_method && (
                      <>
                        <span>•</span>
                        <span>{tx.fiat_method === "sbp" ? t("tx.method_sbp", "СБП") : t("tx.method_card", "Карта")}</span>
                      </>
                    )}
                    {!tx.is_fiat_withdrawal && tx.network && (
                      <>
                        <span>•</span>
                        <img
                          src={getCryptoIconUrl(netSymbol)}
                          alt={netSymbol}
                          className="w-3.5 h-3.5 rounded-full"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = getCryptoIconFallback(netSymbol);
                          }}
                        />
                        <span>{tx.network}</span>
                      </>
                    )}
                  </div>
                )}
                {isCardOp && (
                  <div className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground truncate">
                    <CreditCard className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">
                      {[
                        tx.card_last4 ? `•• ${tx.card_last4}` : null,
                        // Провайдер почти всегда молчит про описание, поэтому
                        // разбираем MCC сами. Сырой код — на самый крайний
                        // случай: соврать про категорию хуже, чем показать код.
                        tx.card_mcc_text ||
                          (mccCategory(tx.card_mcc) ? t(`cards.mcc_${mccCategory(tx.card_mcc)}`) : null) ||
                          (tx.card_mcc ? `MCC ${tx.card_mcc}` : null),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                )}
                {!isCardOp && !isSwap && !isDeposit && !isWithdrawal && (
                  <div className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                      <img
                        src={getCryptoIconUrl(txCurrency)}
                        alt={txCurrency}
                        className="w-3.5 h-3.5 rounded-full"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = getCryptoIconFallback(txCurrency);
                        }}
                      />
                      <span>{txCurrency}</span>
                  </div>
                )}
              </div>
              <div className="w-[108px] flex flex-col items-end justify-center text-right shrink-0 leading-tight">
                <div className={cn("font-mono font-semibold whitespace-nowrap text-[15px]", amountColor)}>
                  {amountStr} {txCurrency}
                </div>
                <div className="text-[12px] text-muted-foreground leading-tight mt-0.5">
                  <div>{date}</div>
                  <div>{time}</div>
                </div>
              </div>
            </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <OperationSheet op={sheet?.op} last4={sheet?.last4} onClose={() => setSheet(null)} />
    </div>
  );
}
